import { useState } from "react";
import type { ContatoIgreja } from "@/core/utils/contatoIgreja";
import type { useProtecaoFormulario } from "@/shared/hooks/useProtecaoSaida";
import {
  FormularioFamilia,
  novaLinhaFamiliar,
  type LinhaFamiliar,
} from "@/modules/visitantes/components/FormularioFamilia";
import { FormularioVisitante } from "@/modules/visitantes/components/FormularioVisitante";
import { PaginaFormulario } from "@/modules/visitantes/components/PaginaFormulario";
import type {
  DadosFamiliar,
  DadosVisitante,
  MinisterioLider,
} from "@/modules/visitantes/services/visitantesService";

interface Props {
  /** "cadastro": ministério responsável cria o visitante. "informe": líder cria o Visitante pendente. */
  modo: "cadastro" | "informe";
  protecao: ReturnType<typeof useProtecaoFormulario>;
  titulo: string;
  descricao: string;
  ministerios?: MinisterioLider[];
  existentes?: { id: string; nome: string }[];
  contatoIgreja?: Partial<ContatoIgreja> | null;
  /** Edição do pendente: dados e familiares já guardados. */
  inicial?: Partial<DadosVisitante>;
  linhasIniciais?: LinhaFamiliar[];
  /** Edição do pendente: o consentimento já foi registrado (não pergunta de novo). */
  consentimentoRegistrado?: boolean;
  enviando: boolean;
  erro?: string | null;
  onLimparErro: () => void;
  onEnviarSimples: (dados: DadosVisitante, consentimento: boolean) => void;
  onEnviarFamilia: (
    principal: DadosVisitante,
    membros: DadosFamiliar[],
    consentimento: boolean,
  ) => void;
  rotuloEnviarFamilia?: (total: number) => string;
  onCancelar: () => void;
}

/**
 * Cadastro completo em até dois passos, igual para o ministério responsável e para o líder que
 * cadastra um Visitante pendente (VIS-DEC-038): etapa 1 = dados da pessoa (aviso e consentimento
 * incluídos); etapa 2 = família. Nada é salvo antes do fim: quem volta à etapa 1 não perde o que
 * digitou na etapa 2.
 */
export function FluxoCadastro({
  modo,
  protecao,
  titulo,
  descricao,
  ministerios,
  existentes,
  contatoIgreja,
  inicial,
  linhasIniciais,
  consentimentoRegistrado = false,
  enviando,
  erro,
  onLimparErro,
  onEnviarSimples,
  onEnviarFamilia,
  rotuloEnviarFamilia,
  onCancelar,
}: Props) {
  const [etapa, setEtapa] = useState<1 | 2>(1);
  const [principal, setPrincipal] = useState<DadosVisitante | null>(null);
  const [linhas, setLinhas] = useState<LinhaFamiliar[]>(() =>
    linhasIniciais && linhasIniciais.length > 0 ? linhasIniciais : [novaLinhaFamiliar()],
  );

  function irParaFamilia(dados: DadosVisitante) {
    onLimparErro();
    setPrincipal(dados);
    setEtapa(2);
    window.scrollTo({ top: 0 });
  }

  if (etapa === 2 && principal) {
    return (
      <PaginaFormulario
        protecao={protecao}
        titulo="Família"
        descricao="Etapa 2 de 2 · quem veio junto"
      >
        <FormularioFamilia
          principal={principal}
          contatoIgreja={contatoIgreja}
          linhas={linhas}
          onLinhas={setLinhas}
          enviando={enviando}
          erro={erro}
          modoInforme={modo === "informe"}
          consentimentoRegistrado={consentimentoRegistrado}
          rotuloEnviar={rotuloEnviarFamilia}
          onCriar={(membros, consentimento) => onEnviarFamilia(principal, membros, consentimento)}
          onVoltar={() => {
            onLimparErro();
            setEtapa(1);
          }}
        />
      </PaginaFormulario>
    );
  }

  return (
    <PaginaFormulario protecao={protecao} titulo={titulo} descricao={descricao}>
      <FormularioVisitante
        modo={modo}
        inicial={principal ?? inicial}
        familiaInicial={principal !== null || (linhasIniciais?.length ?? 0) > 0}
        ministerios={ministerios}
        existentes={existentes}
        enviando={enviando}
        erro={erro}
        contatoIgreja={contatoIgreja}
        consentimentoRegistrado={consentimentoRegistrado}
        onFamilia={irParaFamilia}
        onEnviar={onEnviarSimples}
        onCancelar={onCancelar}
      />
    </PaginaFormulario>
  );
}
